import { defineAsyncComponent } from 'vue';
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router';
import BookerView from '@/views/BookerView/index.vue';
import DashboardView from '@/views/DashboardView/index.vue';
import HomeView from '@/views/HomeView.vue';
import LoginView from '@/views/LoginView.vue';
import PostLoginView from '@/views/PostLoginView.vue';
import { useUserStore } from '@/stores/user-store';
import LogoutView from '@/views/LogoutView.vue';
import BookingConfirmationView from '@/views/BookingConfirmationView.vue';
import { isOidcAuth } from '@/composables/authSchemes';
import { userManager } from '@/composables/oidcUserManager';
import { LOGIN_REDIRECT_KEY } from '@/definitions';

// lazy loaded components
const AvailabilityView = defineAsyncComponent(() => import('@/views/AvailabilityView/index.vue'));
const BookingsView = defineAsyncComponent(() => import('@/views/BookingsView/index.vue'));
const SettingsView = defineAsyncComponent(() => import('@/views/SettingsView/index.vue'));
const LegalView = defineAsyncComponent(() => import('@/views/LegalView.vue'));
const DocsView = defineAsyncComponent(() => import('@/views/DocsView.vue'));
const SubscriberPanelView = defineAsyncComponent(() => import('@/views/admin/SubscriberPanelView.vue'));
const FTUEView = defineAsyncComponent(() => import('@/views/FTUEView/index.vue'));

type ApmtRouteMeta = {
  isPublic?: boolean; // Can the page be accessed without authentication?
  maskForMetrics?: boolean; // Mask url parameters before sending information to metrics
  disableMetrics?: boolean; // Disable all metric capturing for this page. FIXME: Not Impl
};

const redirectUrl = import.meta.env?.VITE_SUPPORT_URL;

/**
 * Defined routes for Thunderbird Appointment
 * Note: All routes require authentication unless otherwise specified in App.vue::routeIsPublic
 */
const routes: RouteRecordRaw[] = [
  // instant loaded routes
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/logout',
    name: 'logout',
    component: LogoutView,
  },
  {
    path: '/post-login/:token?',
    name: 'post-login',
    component: PostLoginView,
    meta: {
      isPublic: true,
      maskForMetrics: true,
    },
  },
  {
    path: '/user/:username/:signatureOrSlug?',
    name: 'booker',
    component: BookerView,
    meta: {
      isPublic: true,
      maskForMetrics: true,
    },
  },
  {
    path: '/user/:username/:signature/confirm/:slot/:token/:confirmed',
    name: 'confirmation',
    component: BookingConfirmationView,
    meta: {
      maskForMetrics: true,
    },
  },
  {
    path: '/availability',
    name: 'availability',
    component: AvailabilityView,
  },
  {
    path: '/schedule',
    name: 'schedule',
    redirect: { name: 'dashboard' },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: DashboardView,
  },
  {
    // This is just to auto-redirect old links. Can be removed in the future.
    path: '/calendar/:date?',
    redirect: { name: 'dashboard' },
  },
  {
    path: '/appointments/:view?/:slug?',
    name: 'appointments',
    redirect: { name: 'bookings' },
  },
  {
    path: '/bookings/:slug?',
    name: 'bookings',
    component: BookingsView,
    meta: {
      maskForMetrics: true,
    },
  },
  {
    path: '/settings/:view?',
    name: 'settings',
    component: SettingsView,
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => {
      window.location.href = redirectUrl;
    },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: LegalView,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/terms',
    name: 'terms',
    component: LegalView,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/docs/using-zoom',
    name: 'using-zoom-docs',
    component: DocsView,
    meta: {
      isPublic: true,
    },
  },
  {
    path: '/setup',
    name: 'setup',
    component: FTUEView,
  },
  // Admin
  {
    path: '/admin/subscribers',
    name: 'admin-subscriber-panel',
    component: SubscriberPanelView,
    meta: {
      // disableMetrics: true,
    },
  },
];

// create router object to export
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  // Ref: https://router.vuejs.org/guide/advanced/scroll-behavior.html
  scrollBehavior(to) {
    if (to.hash) {
      // This ensures that if hash is provided to router.push it works as expected.
      const noPrefersReducedMotion = window.matchMedia('(prefers-reduced-motion: no-preference)').matches;

      return {
        el: to.hash,
        behavior: noPrefersReducedMotion ? 'smooth' : 'auto',
        top: 68 + 16, // Account for the navigation header height (68px) + 16px for some padding
      }
    }
  }
});

router.beforeEach(async (to, _from) => {
  const toMeta: ApmtRouteMeta = to?.meta ?? {};
  const user = useUserStore();

  if (!toMeta?.isPublic && !['setup', 'contact', 'settings', 'logout', 'undefined'].includes(String(to.name))) {    
    if (user && user.data?.email && !user.data.isSetup) {
      return { ...to, name: 'setup' };
    }
  }

  // If the route is not public and the user is not authenticated, redirect to the OIDC login
  if (!toMeta?.isPublic && !user.authenticated && isOidcAuth) {
    // Save their intended destination!
    window.sessionStorage?.setItem(LOGIN_REDIRECT_KEY, to.fullPath);

    await userManager.signinRedirect({});

    return false;
  }
});

export default router;
